'use client';

import { FormControl, FormField, FormItem } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { PhoneNumberInput } from '../../../form-controls/PhoneNumberInput/PhoneNumberInput';
import { ContinueButton } from '../components/ContinueButton/ContinueButton';
import { StepLayout } from '../components/StepLayout/StepLayout';
import { useStepValidation, useValidationErrorHandler } from '../hooks/usePlayerValidation';
import type { FormValues } from '../types';
import { toApiDialCode, transformFormDataToApiPayload } from '../utils/payloadTransformer';

/**
 * Transforms the registration form data for mobile number validation
 */
function transformMobileNumberPayload(formData: FormValues): Record<string, unknown> {
  // Use the shared transformation function as base
  const basePayload = transformFormDataToApiPayload(formData);

  // Override mobile number fields with specific processing (remove whitespace)
  return {
    ...basePayload,
    mobilePhoneNumber: formData.mobileNumber?.number?.replace(/\s+/g, '') || '',
    countryCode: toApiDialCode(formData.mobileNumber?.prefix),
    // Override defaults for mobile number validation
    limitAmount: 0,
    limitPeriod: 'DAY',
  };
}

function MobileNumberStep() {
  const t = useTranslations('form');
  const step = useTranslations('register.screens.mobileNumber');
  const form = useFormContext<FormValues>();
  const { updateFormData, goToNextStep } = useMultistepForm();

  const FORM_ID = 'mobile-number-form';
  const phoneConfig = getSiteConfig().PHONE_NUMBER;
  const lastValidationStatusCode = useRef<number | undefined>(undefined);
  const lastValidationCaughtError = useRef<unknown>(undefined);

  // Step validation hook
  const { validateWithStatus, isValidating: isLoading } = useStepValidation({
    form,
    fieldMapping: {
      mobilePhoneNumber: 'mobileNumber.number',
      countryCode: 'mobileNumber.prefix',
    },
    transformPayload: transformMobileNumberPayload,
  });

  // Error handler for server errors
  const { handleError } = useValidationErrorHandler();

  // This function can be called by the parent when validating this step
  const validateAndUpdateData = async () => {
    const isPrefixValid = await form.trigger('mobileNumber.prefix');
    const isNumberValid = await form.trigger('mobileNumber.number');
    const isFormValid = isPrefixValid && isNumberValid;

    if (isFormValid) {
      // Validate mobile number with API using the reusable hook
      // Note: whitespace removal is handled in the payload transformer
      const {
        success: isApiValid,
        statusCode,
        caughtError,
      } = await validateWithStatus({}, undefined, () => {
        // On successful validation, update the form data
        const phoneNumberData = form.getValues('mobileNumber');
        updateFormData('mobileNumber', phoneNumberData);
      });

      // Store status code for later use in error handling
      lastValidationStatusCode.current = statusCode;
      lastValidationCaughtError.current = caughtError;

      return isApiValid;
    }

    return false;
  };

  const handleContinue = async () => {
    const isValid = await validateAndUpdateData();
    if (isValid) {
      goToNextStep();
    } else {
      // Handle server errors using the reusable error handler
      handleError(lastValidationStatusCode.current, lastValidationCaughtError.current);
      // For validation errors (422), just stay on the current step and show form errors
    }
  };

  const prefixError = form.formState.errors.mobileNumber?.prefix?.message as string | undefined;
  const numberError = form.formState.errors.mobileNumber?.number?.message as string | undefined;
  const helpMessage = numberError || prefixError;

  return (
    <StepLayout title={step('title')} data-testid="mobile-number-step">
      {/* Mobile number form fields */}
      <div className="flex flex-col gap-8 align-center">
        <FormField
          control={form.control}
          name="mobileNumber"
          render={({ field, fieldState }) => (
            <FormItem className="flex-1">
              <FormControl>
                <PhoneNumberInput
                  id={`${FORM_ID}-phone-number`}
                  label={t('labels.mobileNumber')}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  placeholder={phoneConfig.showCountryPrefix ? step('placeholder') : phoneConfig.placeholder}
                  useDynamicPlaceholder={false}
                  formatNationalInput={phoneConfig.formatNationalInput}
                  hidePrefix={!phoneConfig.showCountryPrefix}
                  helpMessage={helpMessage}
                  helpMessageMode="focusout"
                  helpMessageType={fieldState.error ? 'error' : undefined}
                  prefixTrackingProps={{ 'data-track-name': 'Country Code', 'data-track-privacy': 'Mask' }}
                  inputTrackingProps={{ 'data-track-name': 'Mobile', 'data-track-privacy': 'Mask' }}
                  helperTrackingProps={{ 'data-track-helper': 'Mobile' }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {/* Continue button */}
      <div className="space-y-3 text-center mt-auto md:mt-0" data-track-container="Actions">
        <ContinueButton loading={isLoading} onClick={handleContinue} data-track-name="Continue" />
      </div>
    </StepLayout>
  );
}

export { MobileNumberStep, transformMobileNumberPayload };
